import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/uniqueId';
import isNaN from 'lodash/isNaN';
import debounce from 'lodash/debounce';

import { ReactComponent as LocationMarker } from 'assets/images/svg/location-marker.svg';

import container from 'containers/SearchAutocomplete';

import siteStyle from 'utils/siteStyle';

const STRING_LIMIT_SEARCH = 3;
const NUMBER_LIMIT_SEARCH = 3;

export const SearchAutocompleteMobile = ({
  getSearchSuggestions,
  isLoading,
  resetSearchSuggestions,
  searchSuggestions,
  currentLocationLabel,
  setLocation,
  setSearchLocation,
  setValue,
  value,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputId = `input-${uniqueId()}`;

  const debouncedGetSearchSuggestions = useCallback(
    debounce(getSearchSuggestions, 500),
    [getSearchSuggestions],
  );

  const getItemStyle = useCallback((type) => {
    if (type === 'City' || type === 'Zipcode' || type === 'Course') {
      return 'text-4 leading-none hover:text-primary';
    } if (type === 'Current' || type === 'CurrentDisabled') {
      return 'text-4 font-black text-primary leading-none';
    } if (type === 'Recent') {
      return 'text-4 font-medium leading-none hover:text-primary';
    } if (type === 'header') {
      return 'text-4 font-bold leading-none twentyNinth-txt-style';
    }
    return '';
  }, []);

  const shouldSearch = useCallback((newValue) => {
    let lengthLimit = STRING_LIMIT_SEARCH;
    if (!isNaN(parseFloat(newValue))) {
      lengthLimit = NUMBER_LIMIT_SEARCH;
    }
    return (newValue.length >= lengthLimit);
  }, []);

  const onChange = (e) => {
    setIsMenuOpen(true);
    const textSearch = String(e.target.value);
    setValue(textSearch);
    if (shouldSearch(textSearch)) {
      debouncedGetSearchSuggestions(textSearch);
    }
  };

  const onSelect = (item) => {
    setIsMenuOpen(false);
    if (item.type !== 'error' && item.type !== 'CurrentDisabled' && item.type !== 'header') {
      setSearchLocation(item.label);
      setLocation(item);
      setValue(item.label);
    }
  };

  return (
    <div className="mt-6 relative flex">
      <label
        htmlFor={inputId}
        className={siteStyle({
          common: 'hover:border-blue-grey cursor-text px-3 flex items-center border border-light-grey-3 h-14 w-full',
          default: 'rounded-3',
          gd: 'rounded-none',
        })}
        onClick={() => {
          resetSearchSuggestions();
          setValue('');
          setSearchLocation('');
          setIsMenuOpen(false);
        }}
        aria-hidden
      >
        <div className="flex items-center flex-none">
          <LocationMarker className="fill-current text-dark" />
        </div>
        <input
          id={inputId}
          type="text"
          placeholder="City, course, or zip"
          className="text-4 leading-5 -tracking-0.2 text-dark placeholder-grey-3 w-full px-3 focus:outline-none"
          onChange={onChange}
          value={!isFocused && !value ? currentLocationLabel : value}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      </label>
      {shouldSearch(value) && !!searchSuggestions?.length && isMenuOpen && !isLoading && (
        <div
          className="absolute w-full mt-2 border border-light-grey-3 rounded-3 bg-white py-2"
          style={{
            top: '100%',
            boxShadow: 'rgba(10, 18, 69, 0.2) 0px 0.25rem 1rem 0px',
            zIndex: '9999',
            maxHeight: 'calc(100vh - 320px)',
            overflow: 'auto',
          }}
        >
          {searchSuggestions.map((item) => (
            item.type === 'header'
              ? (
                <div
                  key="autocomplete-header"
                  className={`py-4 pl-8 pr-24 text-left cursor-pointer sixth-txt-style flex items-center ${getItemStyle(item.type)}`}
                >
                  {item.label}
                </div>
              )
              : (
                <div
                  aria-hidden
                  onClick={() => onSelect(item)}
                  key={`autcompleteItem.${item.id}`}
                  className={`py-4 pl-8 pr-24 leading-none text-left cursor-pointer text-sixteenth hover:text-primary flex sixth-txt-style items-center ${getItemStyle(item.type)}`}
                >
                  {item.label}
                </div>
              )
          ))}
        </div>
      )}
    </div>
  );
};

SearchAutocompleteMobile.propTypes = {
  getSearchSuggestions: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  resetSearchSuggestions: PropTypes.func.isRequired,
  searchSuggestions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    type: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    distance: PropTypes.number,
    slug: PropTypes.string,
  })).isRequired,
  currentLocationLabel: PropTypes.string.isRequired,
  setLocation: PropTypes.func.isRequired,
  setSearchLocation: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

export default container(SearchAutocompleteMobile);
